
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { InterestRateChange } from '../InterestRateChange';
import { PayoutAdjustment } from '../PayoutAdjustment';
import { RecalculatedSchedule } from '../RecalculatedSchedule';
import { AlertIcon } from '../../BorrowingsIcons';
import { formatCurrency, formatDate } from '../utils';

interface ScheduleChangeTabProps {
  loan: any;
  showRecalculatedSchedule: boolean;
  showInterestRateChange: boolean;
  showPayoutAdjustment: boolean;
  updatedRepayments: any[];
  handleScheduleChangeSave: (newRepayments: any[], event: any) => void;
  handleApproveChanges: () => void;
  handleRejectChanges: () => void;
  handleCancelScheduleChange: () => void;
  setShowInterestRateChange: (show: boolean) => void;
  setShowPayoutAdjustment: (show: boolean) => void;
}

export const ScheduleChangeTab: React.FC<ScheduleChangeTabProps> = ({
  loan,
  showRecalculatedSchedule,
  showInterestRateChange,
  showPayoutAdjustment,
  updatedRepayments,
  handleScheduleChangeSave,
  handleApproveChanges,
  handleRejectChanges,
  handleCancelScheduleChange,
  setShowInterestRateChange,
  setShowPayoutAdjustment,
}) => {
  if (showRecalculatedSchedule) {
    return (
      <RecalculatedSchedule 
        originalRepayments={loan.repayments}
        updatedRepayments={updatedRepayments}
        onApprove={handleApproveChanges}
        onReject={handleRejectChanges}
      />
    );
  } 
  
  if (showInterestRateChange) {
    return (
      <InterestRateChange 
        loan={loan}
        onSave={handleScheduleChangeSave}
        onCancel={handleCancelScheduleChange}
      />
    );
  } 
  
  if (showPayoutAdjustment) {
    return (
      <PayoutAdjustment
        loan={loan}
        onSave={handleScheduleChangeSave}
        onCancel={handleCancelScheduleChange}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Repayment Schedule Changes</CardTitle>
        <CardDescription>
          Modify loan schedule based on interest rate changes or repayment adjustments
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Interest Rate Change
              </CardTitle>
              <CardDescription className="text-xs">
                Update schedule when interest rates change
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm py-2">
              <p>Applicable for floating rate loans. Updates all future repayments based on the new rate.</p>
            </CardContent>
            <CardFooter className="pt-0">
              <Button 
                size="sm" 
                className="w-full"
                onClick={() => setShowInterestRateChange(true)}
                disabled={loan.interestType !== 'Floating'}
              >
                Change Interest Rate
              </Button>
            </CardFooter>
          </Card>
          
          <Card className="border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Payment Adjustments
              </CardTitle>
              <CardDescription className="text-xs">
                Handle prepayments, foreclosures, or moratorium
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm py-2">
              <p>Apply prepayments to reduce EMI or tenure. Set repayment moratorium periods.</p>
            </CardContent>
            <CardFooter className="pt-0">
              <Button 
                size="sm" 
                className="w-full"
                onClick={() => setShowPayoutAdjustment(true)}
              >
                Adjust Payments
              </Button>
            </CardFooter>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
